/**
 * PropertyFilters — Filterleiste der Apartmentliste.
 *
 * Zwei Achsen: Bestand (Aktiv / Archiviert / Alle) als Serverfilter und Stadt als
 * Nachfilterung der geladenen Seite. Der Unterschied steht in der Leiste, nicht nur im Code —
 * ein Filter, der weniger kann als er verspricht, ist schlimmer als keiner (Lehre aus Block E).
 */
import { Pill, SegmentedControl } from '@/shared/components';
import type { PropertyResponse } from '@/shared/api';
import {
  DEFAULT_PROPERTY_FILTERS,
  isPropertiesFiltered,
  type PropertyFilterState,
} from './propertiesListSearch';

const lifecycleOptions = [
  { value: 'Active', label: 'Aktiv' },
  { value: 'Archived', label: 'Archiviert' },
  { value: 'All', label: 'Alle' },
] as const;

interface PropertyFiltersProps {
  filters: PropertyFilterState;
  onChange: (next: PropertyFilterState) => void;
  /** Die ungefiltert geladene Seite — sonst filterte die Städteauswahl sich selbst weg. */
  properties: PropertyResponse[];
}

export function PropertyFilters({ filters, onChange, properties }: PropertyFiltersProps) {
  const cities = Array.from(new Set(properties.map((p) => p.address.city).filter(Boolean))).sort();
  const hasFilters = isPropertiesFiltered({ ...filters, query: '' });

  return (
    <div className="border-line/60 mb-4 flex flex-wrap items-center gap-x-3 gap-y-2 border-b pb-3">
      <span className="text-ink-faint text-[11px] uppercase tracking-wider">Bestand</span>
      <SegmentedControl
        value={filters.lifecycle}
        onChange={(lifecycle) => onChange({ ...filters, lifecycle })}
        options={lifecycleOptions}
      />

      {cities.length > 1 && (
        <>
          <span className="text-ink-faint ml-3 text-[11px] uppercase tracking-wider">
            Stadt auf dieser Seite
          </span>
          <FilterChip
            label="Alle Städte"
            active={filters.city === 'all'}
            onClick={() => onChange({ ...filters, city: 'all' })}
          >
            Alle
          </FilterChip>
          {cities.map((city) => (
            <FilterChip
              key={city}
              label={city}
              active={filters.city === city}
              onClick={() => onChange({ ...filters, city })}
            >
              {city}
            </FilterChip>
          ))}
        </>
      )}

      {hasFilters && (
        <button
          type="button"
          onClick={() => onChange({ ...DEFAULT_PROPERTY_FILTERS, query: filters.query })}
          className="text-ink-soft hover:text-ink ml-auto inline-flex min-h-11 items-center text-xs underline-offset-2 hover:underline"
        >
          Filter zurücksetzen
        </button>
      )}
    </div>
  );
}

function FilterChip({
  active,
  label,
  onClick,
  children,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      aria-label={label}
      // 44 px hoch nach §9, ohne die Leiste optisch aufzublasen: die Pille bleibt klein,
      // die Trefferfläche liegt darum herum.
      className="focus-visible:ring-sage inline-flex min-h-11 items-center rounded-md focus:outline-none focus-visible:ring-2"
    >
      <Pill variant={active ? 'on' : 'default'} size="sm" className="cursor-pointer">
        {children}
      </Pill>
    </button>
  );
}
